/**
 * Happy iframe 通信桥接工具类（重构版）
 * 负责在 ChatBox.vue 和 happy 应用之间建立双向 postMessage 通信通道
 *
 * 重构改进：
 * 1. 添加消息验证
 * 2. 添加 origin 白名单检查
 * 3. 添加握手确认机制（等待 READY 消息）
 * 4. 改进消息队列逻辑
 * 5. 更好的错误处理
 *
 * @class HappyIframeBridge
 * @extends EventEmitter
 */

import { EventEmitter } from 'events';

// 消息类型常量
export const MESSAGE_TYPES = {
  INIT: 'INIT',
  USER_INFO_UPDATE: 'USER_INFO_UPDATE',
  AGENT_INFO_UPDATE: 'AGENT_INFO_UPDATE',
  IM_SESSION_UPDATE: 'IM_SESSION_UPDATE',
  USER_LOGOUT: 'USER_LOGOUT',
  CUSTOM_EVENT: 'CUSTOM_EVENT',
  // 新增消息类型
  READY: 'READY',  // iframe 已准备好
  ACK: 'ACK',      // 消息确认
  ERROR: 'ERROR',  // 错误消息
  SEND_FILES_TO_IM_SESSION: 'SEND_FILES_TO_IM_SESSION',  // 发送文件到IM会话
  NAVIGATE_TO_IM_SESSION: 'NAVIGATE_TO_IM_SESSION',  // 导航到IM会话
  USER_LOGOUT_REQUEST: 'USER_LOGOUT_REQUEST'  // 用户退出登录请求（来自 iframe）
};

/**
 * 配置选项
 */
const DEFAULT_OPTIONS = {
  /** 允许的 iframe origin 白名单，['*'] 表示允许所有（不推荐生产环境） */
  allowedOrigins: ['*'],
  /** 是否等待 READY 消息再发送 INIT，默认 true */
  waitForReady: true,
  /** 等待 READY 超时时间（毫秒），默认 5000ms */
  readyTimeout: 5000,
  /** 消息发送重试次数，默认 3 */
  maxRetries: 3
};

/**
 * Happy iframe 通信桥接类（重构版）
 */
export class HappyIframeBridge extends EventEmitter {
  constructor(options = {}) {
    super();

    // 合并配置
    this.options = { ...DEFAULT_OPTIONS, ...options };

    // iframe 相关
    this.iframe = null;
    this._iframeWindow = null;

    // Origin 配置
    this.allowedOrigins = this.options.allowedOrigins;
    this.targetOrigin = this.allowedOrigins[0] || '*';

    // 状态标志
    this.isReady = false;
    this.hasReceivedReady = false;

    // 消息队列
    this.messageQueue = [];

    // 定时器
    this.readyTimer = null;

    // 绑定消息处理函数
    this.handleMessage = this.handleMessage.bind(this);

    // 监听来自 iframe 的消息
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handleMessage);
    }

    console.log('[HappyIframeBridge] 已创建，配置:', this.options);
  }

  /**
   * 验证消息来源 origin
   */
  validateOrigin(origin) {
    // 如果配置了 '*'，则允许所有来源（不安全）
    if (this.allowedOrigins.includes('*')) {
      return true;
    }

    // 检查是否在白名单中
    return this.allowedOrigins.some(allowed => {
      // 精确匹配
      if (allowed === origin) {
        return true;
      }
      // 通配符匹配（简单实现）
      if (allowed.includes('*')) {
        const pattern = allowed.replace(/\*/g, '.*');
        return new RegExp(`^${pattern}$`).test(origin);
      }
      return false;
    });
  }

  /**
   * 设置 iframe 引用
   * @param {HTMLIFrameElement} iframe - iframe 元素
   */
  setIframe(iframe) {
    if (!iframe) {
      console.warn('[HappyIframeBridge] iframe 元素无效');
      return;
    }

    this.iframe = iframe;

    // 等待 iframe 加载完成
    iframe.onload = () => {
      console.log('[HappyIframeBridge] iframe onload 触发');

      try {
        const contentWindow = iframe.contentWindow;
        if (contentWindow) {
          // 使用 Object.defineProperty 创建不可枚举属性
          // 注意：writable 和 configurable 需要设置为 true，以便在 destroy 时清理
          Object.defineProperty(this, '_iframeWindow', {
            value: contentWindow,
            writable: true,      // 允许重新赋值（在 destroy 时需要）
            enumerable: false,
            configurable: true   // 允许删除或重新定义
          });
        }
      } catch (error) {
        console.warn('[HappyIframeBridge] 无法访问 iframe contentWindow (跨域):', error);
      }

      // 如果配置了等待 READY，则等待 iframe 发送 READY 消息
      if (this.options.waitForReady) {
        console.log('[HappyIframeBridge] 等待 iframe 发送 READY 消息...');

        // 设置超时
        this.readyTimer = setTimeout(() => {
          if (!this.hasReceivedReady) {
            console.warn('[HappyIframeBridge] 等待 READY 消息超时，强制标记为就绪');
            this.markAsReady();
          }
        }, this.options.readyTimeout);

      } else {
        // 不等待 READY，直接标记为就绪
        this.markAsReady();
      }
    };

    // 触发 iframe 设置完成事件
    this.emit('iframeSet', iframe);
  }

  /**
   * 标记为就绪状态
   */
  markAsReady() {
    if (this.isReady) {
      return;
    }

    console.log('[HappyIframeBridge] iframe 已就绪');
    this.isReady = true;

    // 清除超时定时器
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }

    // 发送队列中的消息
    this.flushMessageQueue();

    // 触发就绪事件
    this.emit('ready');
  }

  /**
   * 处理来自 iframe 的消息
   * @param {MessageEvent} event - postMessage 事件
   */
  handleMessage(event) {
    try {
      // 1. 验证消息来源 origin
      if (!this.validateOrigin(event.origin)) {
        console.warn('[HappyIframeBridge] 拒绝来自未授权 origin 的消息:', event.origin);
        return;
      }

      // 2. 验证消息来源是否是我们的 iframe
      if (this.iframe && this._iframeWindow && event.source !== this._iframeWindow) {
        // 不是来自我们的 iframe，忽略
        return;
      }

      const message = event.data;

      // 3. 验证消息格式
      if (!message || typeof message !== 'object' || !message.type) {
        return;
      }

      console.log('[HappyIframeBridge] 收到消息:', message.type, message);

      // 4. 处理特定消息类型
      switch (message.type) {
        case MESSAGE_TYPES.READY:
          this.handleReadyMessage(message);
          break;

        case MESSAGE_TYPES.ACK:
          this.handleAckMessage(message);
          break;

        case MESSAGE_TYPES.ERROR:
          this.handleErrorMessage(message);
          break;

        case 'COPY_TO_CLIPBOARD':
          this.handleCopyToClipboard(message);
          break;

        case MESSAGE_TYPES.NAVIGATE_TO_IM_SESSION:
          this.handleNavigateToImSession(message);
          break;

        case MESSAGE_TYPES.USER_LOGOUT_REQUEST:
          this.handleUserLogoutRequest(message);
          break;

        case MESSAGE_TYPES.CUSTOM_EVENT: {
          // 处理自定义事件
          const eventType = message.data.eventType;
          console.log('[HappyIframeBridge] 收到自定义事件:', eventType, message.data);

          // 根据事件类型触发特定事件
          if (eventType === 'SESSION_STATUS_UPDATE') {
            this.emit('sessionStatusUpdate', message.data);
          } else if (eventType === 'USER_MESSAGE_SENT') {
            this.emit('userMessageSent', message.data);
          }

          // 触发通用自定义事件
          this.emit('customEvent', message.data);
          break;
        }

        default:
          // 触发对应类型的事件
          this.emit(message.type, message.data);
      }

      // 触发通用消息事件
      this.emit('message', message);

    } catch (error) {
      console.error('[HappyIframeBridge] 处理消息时出错:', error);
    }
  }

  /**
   * 处理 READY 消息
   */
  handleReadyMessage(message) {
    console.log('[HappyIframeBridge] 收到 READY 消息');
    this.hasReceivedReady = true;
    this.markAsReady();
    
    // 发送 INIT 消息给 Happy
    this.sendInit();
  }

  /**
   * 处理 ACK 消息
   */
  handleAckMessage(message) {
    console.log('[HappyIframeBridge] 收到 ACK 消息:', message.data);
    this.emit('ack', message.data);
  }

  /**
   * 处理 ERROR 消息
   */
  handleErrorMessage(message) {
    console.error('[HappyIframeBridge] 收到 ERROR 消息:', message.data);
    this.emit('error', message.data);
  }

  /**
   * 处理导航到IM会话消息
   */
  handleNavigateToImSession(message) {
    console.log('[HappyIframeBridge] 处理导航到IM会话:', message.data);
    const { sessionId, sessionType } = message.data;

    if (!sessionId) {
      console.error('[HappyIframeBridge] 导航失败: sessionId 为空');
      return;
    }

    console.log('[HappyIframeBridge] 触发 navigateToImSession 事件:', { sessionId, sessionType });

    // 触发导航事件，让 ChatBox.vue 监听并处理
    this.emit('navigateToImSession', { sessionId, sessionType });
  }

  /**
   * 处理用户退出登录请求（来自 iframe）
   */
  handleUserLogoutRequest(message) {
    console.log('[HappyIframeBridge] 收到来自 iframe 的退出登录请求:', message.data);

    // 触发退出登录事件，让监听者处理
    this.emit('userLogoutRequest', message.data);
  }

  /**
   * 处理复制到剪贴板消息
   */
  handleCopyToClipboard(message) {
    const text = message.text;
    console.log('[HappyIframeBridge] 处理复制请求，文本长度:', text?.length);

    if (!text) {
      console.error('[HappyIframeBridge] 复制文本为空');
      return;
    }

    try {
      // 方案1: 尝试使用 navigator.clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => {
            console.log('[HappyIframeBridge] 通过navigator.clipboard复制成功');
          })
          .catch(err => {
            console.warn('[HappyIframeBridge] navigator.clipboard失败，尝试降级方案:', err);
            this.fallbackCopy(text);
          });
      } else {
        // 降级方案
        this.fallbackCopy(text);
      }
    } catch (err) {
      console.error('[HappyIframeBridge] 复制失败:', err);
      this.fallbackCopy(text);
    }
  }

  /**
   * 降级复制方案（使用 execCommand）
   */
  fallbackCopy(text) {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        console.log('[HappyIframeBridge] 通过execCommand复制成功');
      } else {
        console.error('[HappyIframeBridge] execCommand复制失败');
      }
    } catch (err) {
      console.error('[HappyIframeBridge] 降级复制方案也失败:', err);
    }
  }

  /**
   * 获取 iframe contentWindow（安全方式）
   */
  getIframeWindow() {
    if (this._iframeWindow) {
      return this._iframeWindow;
    }
    if (this.iframe && this.iframe.contentWindow) {
      return this.iframe.contentWindow;
    }
    return null;
  }

  /**
   * 向 iframe 发送消息
   * @param {string} type - 消息类型
   * @param {object} data - 消息数据
   * @param {string} messageId - 可选的消息 ID
   */
  sendMessage(type, data = {}, messageId = null) {
    const message = {
      type,
      data,
      timestamp: Date.now(),
      messageId: messageId || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    if (!this.isReady) {
      // iframe 未就绪，将消息加入队列
      console.log('[HappyIframeBridge] iframe 未就绪，消息已加入队列:', message.type);
      this.messageQueue.push(message);
      return message.messageId;
    }

    try {
      const iframeWindow = this.getIframeWindow();
      if (iframeWindow) {
        iframeWindow.postMessage(message, this.targetOrigin);
        console.log('[HappyIframeBridge] 发送消息:', message.type, message);
        return message.messageId;
      } else {
        console.warn('[HappyIframeBridge] 无法获取 iframe contentWindow，消息加入队列');
        this.messageQueue.push(message);
        return message.messageId;
      }
    } catch (error) {
      console.error('[HappyIframeBridge] 发送消息失败:', error);
      this.messageQueue.push(message);
      return message.messageId;
    }
  }

  /**
   * 发送队列中的消息
   */
  flushMessageQueue() {
    if (this.messageQueue.length === 0) {
      return;
    }

    console.log(`[HappyIframeBridge] 开始发送队列中的 ${this.messageQueue.length} 条消息`);

    const iframeWindow = this.getIframeWindow();
    if (!iframeWindow) {
      console.warn('[HappyIframeBridge] 无法获取 iframe contentWindow，无法发送队列消息');
      return;
    }

    const failedMessages = [];

    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      try {
        iframeWindow.postMessage(message, this.targetOrigin);
        console.log('[HappyIframeBridge] 发送队列消息:', message.type, message);
      } catch (error) {
        console.error('[HappyIframeBridge] 发送队列消息失败:', error);
        failedMessages.push(message);
      }
    }

    // 将发送失败的消息重新加入队列
    if (failedMessages.length > 0) {
      console.warn(`[HappyIframeBridge] ${failedMessages.length} 条消息发送失败，重新加入队列`);
      this.messageQueue.push(...failedMessages);
    }
  }

  /**
   * 发送初始化消息
   * @param {object} userInfo - 用户信息
   * @param {object} agentInfo - 智能体信息（可选）
   * @param {object} imSessionInfo - IM 会话信息（可选）
   */
  sendInit(userInfo, agentInfo = null, imSessionInfo = null) {
    const data = { userInfo };
    if (agentInfo) {
      data.agentInfo = agentInfo;
    }
    if (imSessionInfo) {
      data.imSessionInfo = imSessionInfo;
    }
    return this.sendMessage(MESSAGE_TYPES.INIT, data);
  }

  /**
   * 发送用户信息更新
   * @param {object} userInfo - 用户信息
   */
  sendUserInfoUpdate(userInfo) {
    return this.sendMessage(MESSAGE_TYPES.USER_INFO_UPDATE, { userInfo });
  }

  /**
   * 发送智能体信息更新
   * @param {object} agentInfo - 智能体信息
   */
  sendAgentInfoUpdate(agentInfo) {
    return this.sendMessage(MESSAGE_TYPES.AGENT_INFO_UPDATE, { agentInfo });
  }

  /**
   * 发送 IM 会话信息更新
   * @param {object} imSessionInfo - IM 会话信息
   */
  sendImSessionInfoUpdate(imSessionInfo) {
    return this.sendMessage(MESSAGE_TYPES.IM_SESSION_UPDATE, { imSessionInfo });
  }

  /**
   * 发送用户退出登录通知
   */
  sendUserLogout() {
    return this.sendMessage(MESSAGE_TYPES.USER_LOGOUT, {
      timestamp: Date.now()
    });
  }

  /**
   * 发送自定义事件
   * @param {string} eventType - 事件类型
   * @param {object} eventData - 事件数据
   */
  sendCustomEvent(eventType, eventData = {}) {
    return this.sendMessage(MESSAGE_TYPES.CUSTOM_EVENT, {
      eventType,
      ...eventData
    });
  }

  /**
   * 发送文件到IM会话
   * @param {Array} files - 文件列表 [{path, name, isDir}, ...]
   * @param {object} session - 目标会话信息 {id, targetId, type, name}
   */
  sendFilesToImSession(files, session) {
    console.log('[HappyIframeBridge] 请求发送文件到IM会话:', {
      filesCount: files.length,
      sessionName: session.name,
      sessionType: session.type
    });

    return this.sendMessage(MESSAGE_TYPES.SEND_FILES_TO_IM_SESSION, {
      files,
      session
    });
  }

  /**
   * 检查是否就绪
   */
  getIsReady() {
    return this.isReady;
  }

  /**
   * 检查是否已接收 READY 消息
   */
  getHasReceivedReady() {
    return this.hasReceivedReady;
  }

  /**
   * 清理资源
   */
  destroy() {
    // 清除定时器
    if (this.readyTimer) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }

    // 移除消息监听
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.handleMessage);
    }

    // 清理状态
    this.iframe = null;
    this._iframeWindow = null;
    this.isReady = false;
    this.hasReceivedReady = false;
    this.messageQueue = [];

    // 移除所有事件监听器
    this.removeAllListeners();

    console.log('[HappyIframeBridge] 已销毁');
  }
}
